import { Box, Button, CircularProgress, Stack, TextField } from '@chronosphereio/chrono-ui';
import { Creation as SparkleIcon } from 'mdi-material-ui';
import type { FieldGenerateButtonProps } from '@/plugin-system';

export interface FieldWithGenerateButtonProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  generate?: FieldGenerateButtonProps;
}

/**
 * Renders a text field with an explicit Generate / Regenerate button and optional Undo.
 * Alternative to FieldWithSuggestion for button-triggered AI generation flows.
 */
export function FieldWithGenerateButton({ value, onChange, label, generate }: FieldWithGenerateButtonProps) {
  if (!generate) {
    return <TextField fullWidth label={label} value={value} onChange={(e) => onChange(e.target.value)} />;
  }

  const { onGenerate, isGenerating, hasGenerated, onUndo } = generate;
  const buttonLabel = hasGenerated ? 'Regenerate' : 'Generate';

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                height: '100%',
                alignItems: 'center',
                justifyContent: 'flex-end',
                marginY: -0.25,
                marginRight: -1,
              }}
            >
              <Button
                variant="tertiary"
                size="small"
                disabled={isGenerating}
                onClick={onGenerate}
                StartIcon={isGenerating ? <CircularProgress size={14} /> : <SparkleIcon fontSize="small" />}
              >
                {isGenerating ? 'Generating...' : buttonLabel}
              </Button>
              {onUndo && (
                <Button variant="tertiary" size="small" disabled={isGenerating} onClick={onUndo}>
                  Undo
                </Button>
              )}
            </Stack>
          ),
        }}
      />
    </Box>
  );
}
